/**
 * Performance optimization utilities for smooth scrolling
 */

/**
 * Check if device prefers reduced motion
 * @returns {boolean}
 */
export const prefersReducedMotion = () => {
  if (typeof window === 'undefined') return false;

  return window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
};

/**
 * Check if device is low-end based on available metrics
 * @returns {boolean}
 */
export const isLowEndDevice = () => {
  if (typeof navigator === 'undefined') return false;

  // Check device memory (if available)
  if (navigator.deviceMemory && navigator.deviceMemory < 4) {
    return true;
  }

  // Check hardware concurrency (CPU cores)
  if (navigator.hardwareConcurrency && navigator.hardwareConcurrency < 4) {
    return true;
  }

  // Check connection (if available)
  if (navigator.connection && navigator.connection.effectiveType) {
    const slowConnections = ['slow-2g', '2g', '3g'];
    if (slowConnections.includes(navigator.connection.effectiveType)) {
      return true;
    }
  }

  return false;
};

/**
 * Get optimized physics config based on device capabilities
 * @returns {Object} - Optimized physics configuration
 */
export const getOptimizedPhysicsConfig = () => {
  const baseConfig = {
    FRICTION: {
      NORMAL: 0.88,      // More controlled
      FAST: 0.82,        // More aggressive for fast scrolling
      SLOW: 0.92,        // Still smooth for slow
      BOUNDARY: 0.75     // Strong boundary friction
    },
    VELOCITY: {
      THRESHOLD: 1.0,    // Higher threshold to stop sooner
      MAX: 15,           // Much lower max velocity
      MIN_FOR_MOMENTUM: 2.0 // Higher minimum for momentum
    },
    PERFORMANCE: {
      THROTTLE_MS: 16,
      MAX_POINTS: 3,     // Fewer points for less sensitive velocity
      TIME_WINDOW: 80    // Shorter time window
    }
  };

  // Adjust for reduced motion preference
  if (prefersReducedMotion()) {
    return {
      ...baseConfig,
      FRICTION: {
        NORMAL: 0.85, // More aggressive friction
        FAST: 0.8,
        SLOW: 0.9,
        BOUNDARY: 0.7
      },
      VELOCITY: {
        ...baseConfig.VELOCITY,
        THRESHOLD: 0.5, // Higher threshold to stop sooner
        MAX: 20, // Lower max velocity
        MIN_FOR_MOMENTUM: 2 // Higher minimum for momentum
      }
    };
  }

  // Adjust for low-end devices
  if (isLowEndDevice()) {
    return {
      ...baseConfig,
      PERFORMANCE: {
        THROTTLE_MS: 32, // Lower frame rate
        MAX_POINTS: 3, // Fewer tracking points
        TIME_WINDOW: 150 // Longer time window
      },
      VELOCITY: {
        ...baseConfig.VELOCITY,
        MAX: 30 // Lower max velocity for smoother performance
      }
    };
  }

  return baseConfig;
};

/**
 * RequestAnimationFrame with fallback for older browsers
 * @param {Function} callback
 * @returns {number} - Animation frame ID
 */
export const requestAnimFrame = (() => {
  return (
    window.requestAnimationFrame ||
    window.webkitRequestAnimationFrame ||
    window.mozRequestAnimationFrame ||
    function(callback) {
      window.setTimeout(callback, 1000 / 60);
    }
  );
})();

/**
 * CancelAnimationFrame with fallback for older browsers
 * @param {number} id - Animation frame ID
 */
export const cancelAnimFrame = (() => {
  return (
    window.cancelAnimationFrame ||
    window.webkitCancelAnimationFrame ||
    window.mozCancelAnimationFrame ||
    function(id) {
      clearTimeout(id);
    }
  );
})();

/**
 * Passive event listener support detection
 * @returns {boolean|Object}
 */
export const getPassiveEventListenerOptions = () => {
  let supportsPassive = false;

  try {
    const opts = Object.defineProperty({}, 'passive', {
      get() {
        supportsPassive = true;
        return true;
      }
    });

    window.addEventListener('test', null, opts);
    window.removeEventListener('test', null, opts);
  } catch (e) {
    // Passive not supported
  }

  return supportsPassive ? { passive: true } : false;
};

/**
 * Memory-efficient event listener management
 */
export class EventListenerManager {
  constructor() {
    this.listeners = new Map();
  }

  add(element, event, handler, options = false) {
    const key = `${event}-${handler.name || 'anonymous'}`;

    if (this.listeners.has(key)) {
      this.remove(element, event, this.listeners.get(key));
    }

    element.addEventListener(event, handler, options);
    this.listeners.set(key, handler);
  }

  remove(element, event, handler) {
    const key = `${event}-${handler.name || 'anonymous'}`;

    if (handler) {
      element.removeEventListener(event, handler);
      this.listeners.delete(key);
    }
  }

  removeAll(element) {
    this.listeners.forEach((handler, key) => {
      const [event] = key.split('-');
      element.removeEventListener(event, handler);
    });
    this.listeners.clear();
  }
}

/**
 * Performance monitoring for smooth scrolling
 */
export class PerformanceMonitor {
  constructor() {
    this.frameCount = 0;
    this.lastTime = Date.now();
    this.fps = 60;
    this.isMonitoring = false;
  }

  start() {
    this.isMonitoring = true;
    this.frameCount = 0;
    this.lastTime = Date.now();
    this._monitor();
  }

  stop() {
    this.isMonitoring = false;
  }

  _monitor() {
    if (!this.isMonitoring) return;

    this.frameCount++;
    const currentTime = Date.now();

    if (currentTime - this.lastTime >= 1000) {
      this.fps = this.frameCount;
      this.frameCount = 0;
      this.lastTime = currentTime;

      // Log performance warning if FPS drops significantly
      if (this.fps < 45) {
        console.warn(`Smooth scrolling performance warning: ${this.fps} FPS`);
      }
    }

    requestAnimFrame(() => this._monitor());
  }

  getFPS() {
    return this.fps;
  }

  isPerformanceGood() {
    return this.fps >= 45;
  }
}